import React, { useCallback, useEffect, useState } from 'react'

import { Box, Button, CircularProgress, Link as MuiLink, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'

import Title from 'components/Title'

import UnsubscribeReason from './UnsubscribeReason'
import { checkUnsubscribeStatus, resubscribe, unsubscribe } from './service'

const useStyles = makeStyles(() => ({
  mainContent: {
    textAlign: 'left',
  },
  actionContainer: {
    textAlign: 'center',
  },
  submitButton: {
    borderRadius: 40,
    width: '100%',
  },
}))

type UnsubscribeInvitesProps = {
  inviteToken: string
  messageTag: string
  subToken: string
  cleverRequired: boolean
}

const redirectInvite = (inviteToken: string, cleverRequired: boolean) =>
  window.location.assign(
    cleverRequired ? `/confirm/student/clever-signup/${inviteToken}` : `/confirm/student/signup/${inviteToken}`,
  )

export const APPLICATION_DEADLINE_REMINDER_TAG = 'upcoming-application-deadline-reminder'
export const COLLEGE_OUTREACH_TAG = 'CollegeOutreach'
export const PARENT_VISIT_NOTIFICATION_TAG = 'ParentVisitNotification'
export const STUDENT_VISIT_NOTIFICATION_TAG = 'StudentVisitNotification'

export type UnsubscribeType = 'default' | 'appDeadline' | 'collegeOutreach' | 'parentVisit' | 'studentVisit'

type CopyConfig = {
  title: string
  unsubscribedTitle?: string
  resubscribedTitle?: string
  hasUnsubscribed: React.ReactNode
  hasResubscribed: React.ReactNode
  promptUnsubscribed: React.ReactNode
  regret: string
  submit: string
  doNotSubmitRegret: string
  hideReason?: boolean
}

const DEFAULT_COPY_CONFIG: CopyConfig = {
  title: 'We\u2019re sad to see you go \uD83D\uDC94',
  hasUnsubscribed: `We won't send you any more reminders about this invite, but your counselor may still send you emails via Scoir.`,
  hasResubscribed: `We will still send you reminders about this invite.`,
  promptUnsubscribed: `Please let us know why you're not interested in joining Scoir. We'd love your feedback.`,
  regret: 'I have regrets. I want to try Scoir (and keep receiving emails).',
  submit: 'Submit',
  doNotSubmitRegret: `Never mind, I'd like to try Scoir`,
}
const HasUnsub = () => (
  <>
    You&apos;ve successfully unsubscribed from application reminders. You can still{' '}
    <Link to={`${import.meta.env.BASE_URL}/signin`}>log in</Link> to Scoir to apply to colleges and track deadlines.
  </>
)
const HasResub = () => (
  <>
    Great, you&apos;ll still receive application reminders.{' '}
    <Link to={`${import.meta.env.BASE_URL}/signin`}>Log in</Link> to Scoir to apply to colleges and track deadlines.
  </>
)
const HasOutreachUnsub = () => (
  <>
    You&apos;ll no longer receive email notifications about unread college messages on Scoir.
    <br />
    <br />
    You can still <Link to={`${import.meta.env.BASE_URL}`}>log in to Scoir</Link> to read messages from colleges
    directly.
  </>
)
const PromptOutreachUnsub = () => (
  <>
    You&apos;ll stop receiving email notifications about any unread college message on Scoir.
    <br />
    <br />
    Your college list won&apos;t be affected. You&apos;ll still receive college messages directly on Scoir &mdash; you
    just won&apos;t get email notifications about them. To stop hearing from a specific college entirely, unfollow them
    or mark them not interested on Scoir (or ask your student to if you&apos;re a parent!).
  </>
)
const HasOutreachResub = () => (
  <>
    You&apos;ll continue receiving email notifications when colleges message you on Scoir.
    <br />
    <br />
    <Link to={`${import.meta.env.BASE_URL}`}>Log in to Scoir</Link> to view your messages.
  </>
)
const HasParentVisitUnsub = () => (
  <>
    You&apos;ll no longer receive email notifications about college visits scheduled at your child&apos;s high school.
    <br />
    <br />
    <Link to={`${import.meta.env.BASE_URL}`}>Log in to Scoir</Link> to view upcoming visits.
  </>
)
const PromptParentVisitUnsub = () => (
  <>
    You&apos;ll stop receiving email notifications about college visits scheduled at your child&apos;s high school. You
    can still look for updates on your homepage or in your Inbox on Scoir.
  </>
)
const HasParentVisitResub = () => (
  <>
    You&apos;ll continue receiving email notifications about college visits scheduled at your child&apos;s high school.
    <br />
    <br />
    <Link to={`${import.meta.env.BASE_URL}`}>Log in to Scoir</Link> to view upcoming visits.
  </>
)
const HasStudentVisitUnsub = () => (
  <>
    You&apos;ll no longer receive email notifications about college visits scheduled at your high school.
    <br />
    <br />
    <Link to={`${import.meta.env.BASE_URL}`}>Log in to Scoir</Link> to view upcoming visits.
  </>
)
const PromptStudentVisitUnsub = () => (
  <>
    You&apos;ll stop receiving email notifications about college visits scheduled at your high school. You can still
    look for updates on your homepage or in your Inbox on Scoir.
  </>
)
const HasStudentVisitResub = () => (
  <>
    You&apos;ll continue receiving email notifications about college visits scheduled at your high school.
    <br />
    <br />
    <Link to={`${import.meta.env.BASE_URL}`}>Log in to Scoir</Link> to view upcoming visits.
  </>
)
const HasError = ({ action }: { action: string }) => (
  <>
    An error occurred while {action}. Please contact Scoir{' '}
    <MuiLink
      href="https://www.scoir.com/support"
      target="_blank"
      rel="noopener noreferrer"
    >
      Support
    </MuiLink>{' '}
    for assistance.
  </>
)
const APP_DEADLINE_COPY_CONFIG = {
  ...DEFAULT_COPY_CONFIG,
  title: `Don't want these emails?`,
  hasUnsubscribed: <HasUnsub />,
  hasResubscribed: <HasResub />,
  promptUnsubscribed: `Please let us know why you're not interested in receiving application reminders. We'd love your feedback.`,
  regret: `Never mind, I'd like to keep receiving reminders`,
  submit: 'Submit',
  doNotSubmitRegret: `Never mind, I'd like to keep receiving reminders`,
}
const COLLEGE_OUTREACH_COPY_CONFIG = {
  ...DEFAULT_COPY_CONFIG,
  title: `Unsubscribe from college outreach email notifications?`,
  unsubscribedTitle: `You've been unsubscribed`,
  resubscribedTitle: `You're all set`,
  hasUnsubscribed: <HasOutreachUnsub />,
  hasResubscribed: <HasOutreachResub />,
  promptUnsubscribed: <PromptOutreachUnsub />,
  regret: `Undo — resubscribe to email notifications`,
  submit: 'Yes, unsubscribe',
  doNotSubmitRegret: `No, keep my email notifications`,
  hideReason: true,
}
const PARENT_VISIT_COPY_CONFIG = {
  ...DEFAULT_COPY_CONFIG,
  title: `Unsubscribe from college visit notification emails?`,
  unsubscribedTitle: `You've been unsubscribed`,
  resubscribedTitle: `You're all set`,
  hasUnsubscribed: <HasParentVisitUnsub />,
  hasResubscribed: <HasParentVisitResub />,
  promptUnsubscribed: <PromptParentVisitUnsub />,
  regret: `Undo — resubscribe to email notifications`,
  submit: 'Yes, unsubscribe',
  doNotSubmitRegret: `No, keep my email notifications`,
  hideReason: true,
}
const STUDENT_VISIT_COPY_CONFIG = {
  ...DEFAULT_COPY_CONFIG,
  title: `Unsubscribe from college visit notification emails?`,
  unsubscribedTitle: `You've been unsubscribed`,
  resubscribedTitle: `You're all set`,
  hasUnsubscribed: <HasStudentVisitUnsub />,
  hasResubscribed: <HasStudentVisitResub />,
  promptUnsubscribed: <PromptStudentVisitUnsub />,
  regret: `Undo — resubscribe to email notifications`,
  submit: 'Yes, unsubscribe',
  doNotSubmitRegret: `No, keep my email notifications`,
  hideReason: true,
}

const UnsubscribeInvites = ({ inviteToken, messageTag, subToken, cleverRequired }: UnsubscribeInvitesProps) => {
  const classes = useStyles()
  const [reason, setReason] = useState('')
  const [otherReason, setOtherReason] = useState('')
  const [hasUnsubscribeError, setHasUnsubscribeError] = useState(false)
  const [hasResubscribeError, setHasResubscribeError] = useState(false)
  const [hasUnsubscribed, setHasUnsubscribed] = useState(false)

  const [hasResubscribed, setHasResubscribed] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoadingStatus, setIsLoadingStatus] = useState(true)
  const getUnsubscribeType = (tag: string): UnsubscribeType => {
    switch (tag) {
      case APPLICATION_DEADLINE_REMINDER_TAG:
        return 'appDeadline'
      case COLLEGE_OUTREACH_TAG:
        return 'collegeOutreach'
      case PARENT_VISIT_NOTIFICATION_TAG:
        return 'parentVisit'
      case STUDENT_VISIT_NOTIFICATION_TAG:
        return 'studentVisit'
      default:
        return 'default'
    }
  }
  const unsubscribeType: UnsubscribeType = getUnsubscribeType(messageTag)

  const copyConfigs: Record<UnsubscribeType, CopyConfig> = {
    default: DEFAULT_COPY_CONFIG,
    appDeadline: APP_DEADLINE_COPY_CONFIG,
    collegeOutreach: COLLEGE_OUTREACH_COPY_CONFIG,
    parentVisit: PARENT_VISIT_COPY_CONFIG,
    studentVisit: STUDENT_VISIT_COPY_CONFIG,
  }
  const copyConfig = copyConfigs[unsubscribeType]

  useEffect(() => {
    document.title = 'Scoir - Unsubscribe'
    checkUnsubscribeStatus(messageTag, subToken)
      .then((res) => {
        if (res?.Unsubscribed) {
          setHasUnsubscribed(true)
        }
      })
      .catch(() => {
        // If status check fails, default to showing the unsubscribe flow
      })
      .finally(() => setIsLoadingStatus(false))
  }, [messageTag, subToken])

  const submit = useCallback(() => {
    setHasUnsubscribeError(false)
    setIsSubmitting(true)
    unsubscribe(reason, otherReason, messageTag, subToken)
      .then(() => setHasUnsubscribed(true))
      .catch(() => setHasUnsubscribeError(true))
      .finally(() => setIsSubmitting(false))
  }, [reason, otherReason, messageTag, subToken])

  const undoSubmit = useCallback(() => {
    setHasResubscribeError(false)
    setIsSubmitting(true)
    resubscribe(messageTag, subToken)
      .then(() => {
        if (unsubscribeType === 'default') {
          redirectInvite(inviteToken, cleverRequired)
        } else {
          setHasResubscribed(true)
        }
      })
      .catch(() => {
        setIsSubmitting(false)
        setHasResubscribeError(true)
      })
  }, [messageTag, unsubscribeType, subToken, inviteToken, cleverRequired])

  if (isLoadingStatus) {
    return (
      <Box>
        <Title title={copyConfig.title} subtitle="" />
        <Box textAlign="center" mt={2}>
          <CircularProgress />
        </Box>
      </Box>
    )
  }

  return (
    <Box>
      <Title
        title={
          hasResubscribed && copyConfig.resubscribedTitle
            ? copyConfig.resubscribedTitle
            : hasUnsubscribed && copyConfig.unsubscribedTitle
              ? copyConfig.unsubscribedTitle
              : copyConfig.title
        }
        subtitle={
          hasResubscribed
            ? copyConfig.hasResubscribed
            : hasUnsubscribeError || hasResubscribeError
              ? ''
              : hasUnsubscribed
                ? copyConfig.hasUnsubscribed
                : copyConfig.promptUnsubscribed
        }
      />
      <Box className={classes.mainContent}>
        {!hasResubscribed && (
          <>
            {!hasUnsubscribed &&
              (copyConfig.hideReason ? (
                <Box mb={4} />
              ) : (
                <Box mb={2}>
                  <UnsubscribeReason
                    unsubscribeType={unsubscribeType}
                    reason={reason}
                    setReason={setReason}
                    otherReason={otherReason}
                    setOtherReason={setOtherReason}
                  />
                </Box>
              ))}
            {(hasUnsubscribeError || hasResubscribeError) && (
              <Box mb={2}>
                <Typography
                  role="alert"
                  align="center"
                  color="error"
                >
                  {hasUnsubscribeError && <HasError action="unsubscribing" />}
                  {hasResubscribeError && <HasError action="resubscribing" />}
                </Typography>
              </Box>
            )}
            {!hasUnsubscribeError && !hasResubscribeError && (
              <Box className={classes.actionContainer}>
                <Box mb={2}>
                  {hasUnsubscribed ? (
                    <Button
                      color="primary"
                      size="large"
                      disabled={isSubmitting}
                      onClick={undoSubmit}
                    >
                      {copyConfig.regret}
                    </Button>
                  ) : (
                    <Button
                      className={classes.submitButton}
                      variant="contained"
                      color="primary"
                      disabled={(!copyConfig.hideReason && !reason) || isSubmitting}
                      onClick={submit}
                    >
                      {copyConfig.submit}
                    </Button>
                  )}
                </Box>
                {!hasUnsubscribed && (
                  <Box mb={2}>
                    <Button
                      color="primary"
                      size="large"
                      onClick={() => {
                        if (unsubscribeType === 'default') {
                          redirectInvite(inviteToken, cleverRequired)
                        } else {
                          setHasResubscribed(true)
                        }
                      }}
                    >
                      {copyConfig.doNotSubmitRegret}
                    </Button>
                  </Box>
                )}
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  )
}

UnsubscribeInvites.propTypes = {
  inviteToken: PropTypes.string.isRequired,
  messageTag: PropTypes.string.isRequired,
  subToken: PropTypes.string.isRequired,
  cleverRequired: PropTypes.bool,
}

export default UnsubscribeInvites
